/**
 * useMarkNotificationRead — mutation hook for FR-005 (single-row markRead).
 *
 * Wraps `api.notifications.markRead` and returns the flat
 * `NotificationMutationState` shape. Both `action` (via `useCallback`)
 * and the return object (via `useMemo`) are reference-stable across
 * renders when dependencies do not change (Finding C-2).
 *
 * Error discrimination uses `instanceof ConvexError` for application-vs-
 * platform errors. `console.error` is reserved for non-ConvexError
 * exceptions (CLAUDE.md §5).
 */
import { useCallback, useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import type { Id } from "../../../../../convex/_generated/dataModel";
import type { NotificationMutationState } from "../types/notification";

/** Args shape for the markRead action. */
interface MarkReadArgs {
  notificationId: Id<"notifications">;
}

/** Result shape from the markRead mutation. */
interface MarkReadResult {
  ok: true;
  alreadyRead: boolean;
}

/**
 * Named hook wrapping `api.notifications.markRead`.
 *
 * @returns Flat `NotificationMutationState<MarkReadArgs, MarkReadResult>` with
 *   stable `action` reference across renders.
 */
export function useMarkNotificationRead(): NotificationMutationState<
  MarkReadArgs,
  MarkReadResult
> {
  const mutate = useMutation(api.notifications.markRead);

  const [isMarking, setIsMarking] = useState(false);
  const [status, setStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [lastResult, setLastResult] = useState<MarkReadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const action = useCallback(
    (args: MarkReadArgs) => {
      setIsMarking(true);
      setStatus("pending");
      setError(null);

      mutate({ notificationId: args.notificationId })
        .then((result) => {
          const typed = result as MarkReadResult;
          setIsMarking(false);
          setStatus("success");
          setLastResult(typed);
        })
        .catch((err: unknown) => {
          setIsMarking(false);
          setStatus("error");
          const message =
            err instanceof Error ? err.message : String(err);
          setError(message);
          if (!(err instanceof Error && "data" in err)) {
            console.error(err);
          }
        });
    },
    [mutate],
  );

  return useMemo(
    () => ({ action, isMarking, status, lastResult, error }),
    [action, isMarking, status, lastResult, error],
  );
}
